/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 2026-08-24
 * @author      : Claude (requested by Aravindan)
 * @since       : 1.5.0.0
 * Requirement# : Provider parity for the tenant-side Review Thread. The tenant already sees, per
 *                template family, a cycle-by-cycle review conversation (TemplateReviewThread.tsx in
 *                tenant-web) plus a Versions link. The provider admin had only a Version History
 *                table and no thread view — this closes that gap so a reviewer sees the same story.
 * Purpose      : Read-only dialog showing every review cycle of ONE template family, newest work
 *                oldest-first, each cycle = a version row's submission + GMS's decision + note.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * Provider thread| 2026-08-24  | Claude   | Initial creation
 */

import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { AsyncBoundary, useAsyncResource } from './AsyncStates';
import { fetchTemplateVersions } from './MessageTemplateService';
import {
  formatUtcDateTime,
  requestTypeLabel,
  statusColor,
  statusLabel,
  TemplateSummary,
} from './CommunicationTypes';
import { TemplateVersionHistory } from './TemplateVersionHistory';

/**
 * The review-cycle history of ONE template family, as the PROVIDER sees it — parity with the
 * tenant's own {@code TemplateReviewThread}.
 *
 * <h2>Why cycles are built from version rows, not change-request rows</h2>
 * The tenant builds its thread from {@code communication_template_change_request} rows (its own
 * submissions). The provider has no per-family change-request list endpoint, but it does have
 * {@code GET /message-templates/{uid}/versions}, and every version row already carries the exact
 * fields a cycle needs — {@code status}, {@code reviewNotes}, {@code reviewedBy}, {@code reviewedAt},
 * {@code createdAt} — via the shared {@code toSummaryDTO}. A version IS a submission→decision round
 * (v1 = the new template, vN>1 = a revision), so one version = one cycle. This mirrors the tenant
 * thread's information without a new backend endpoint, and is in fact MORE complete for the
 * provider's oversight role because it includes provider-made edits too.
 *
 * <p><strong>Read-only, always.</strong> No approve/reject/publish here — those live on Review
 * Detail. The "Compare versions" button opens {@link TemplateVersionHistory} rather than
 * duplicating the diff engine, exactly as the tenant thread does.
 */
export interface TemplateReviewThreadProps {
  /** Any TemplateSummary for the family — only templateMasterUid/name are read from it. */
  template: TemplateSummary;
  onClose: () => void;
}

export const TemplateReviewThread: React.FC<TemplateReviewThreadProps> = ({ template, onClose }) => {
  const { accessToken } = useOidcAccessToken();
  const [showVersions, setShowVersions] = useState(false);

  const { state, reload } = useAsyncResource<TemplateSummary[]>(
    () => fetchTemplateVersions(template.templateMasterUid, accessToken),
    [template.templateMasterUid, accessToken],
  );

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-testid="review-thread-dialog">
        <DialogTitle>Review thread — {template.name}</DialogTitle>
        <DialogContent dividers>
          <AsyncBoundary
            state={state}
            what="Review thread"
            onRetry={reload}
            loadingLabel="Loading review thread…"
            isEmpty={(versions) => versions.length === 0}
            emptyTitle="No review history found for this template."
          >
            {(versions) => <ThreadContent versions={versions} />}
          </AsyncBoundary>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVersions(true)} data-testid="review-thread-compare-button">
            Compare versions
          </Button>
          <Button onClick={onClose} data-testid="review-thread-close-button">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {showVersions && (
        <Box data-testid="review-thread-versions">
          <TemplateVersionHistory template={template} onClose={() => setShowVersions(false)} />
        </Box>
      )}
    </>
  );
};

// A version has a real GMS decision only in these terminal/decided states. UNDER_REVIEW and DRAFT
// are "awaiting", even when reviewedBy/reviewedAt carry over from a prior cycle on the same row.
const DECIDED_STATUSES = new Set(['APPROVED', 'PUBLISHED', 'REJECTED', 'CHANGES_REQUESTED', 'SUPERSEDED']);

const ThreadContent: React.FC<{ versions: TemplateSummary[] }> = ({ versions }) => {
  // Oldest first — a thread reads top-to-bottom in time. The server sorts newest-first, so reverse
  // by version number (stable, unlike re-parsing timestamps that can be null on unpublished rows).
  const ordered = [...versions].sort((a, b) => a.version - b.version);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each cycle below is one version — a submission and GMS&apos;s decision on it. The reviewer&apos;s
        note (when present) is shown under each decision.
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="review-cycle-count">
        {ordered.length} review cycle{ordered.length === 1 ? '' : 's'}
      </Typography>

      <Stack spacing={2} data-testid="review-thread-list">
        {ordered.map((v, i) => (
          <Paper key={v.version} variant="outlined" sx={{ p: 2 }} data-testid={`review-cycle-${i + 1}`}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                Cycle {i + 1} · v{v.version} · {requestTypeLabel(v.version)}
              </Typography>
              <Chip size="small" label={statusLabel(v.status)} color={statusColor(v.status)} />
            </Stack>

            {/* Submission side — origin (tenant vs provider) + when the version row was created. */}
            <Typography variant="body2">
              <strong>{v.submittedByTenant ? `Submitted by tenant ${v.submittedByTenant}` : 'Submitted by provider'}</strong>{' '}
              {formatUtcDateTime(v.createdAt ?? null) ?? '—'}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {/* GMS decision side — reviewer + time + note. */}
            {DECIDED_STATUSES.has(v.status) ? (
              <>
                <Typography variant="body2">
                  <strong>GMS {statusLabel(v.status).toLowerCase()}</strong>{' '}
                  {formatUtcDateTime(v.reviewedAt) ?? ''}
                  {v.reviewedBy ? ` · ${v.reviewedBy}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {v.reviewNotes ? v.reviewNotes : 'No note left.'}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Awaiting review.
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </>
  );
};

export default TemplateReviewThread;
